import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  ADD_EXCHANGE_RETURN_FLOW,
  EXCHANGE_RETURN_UI_NAME,
  PAYMENT_MODE_OPTIONS,
  PRODUCT_DETAIL_FIELDS,
} from '../../common/constant/page-constant'
import { formatDate, formatTime } from '../../common/utils/appUtils'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import './ExchangeReturn.scss'

import DownloadIcon from '../../assets/DownloadIcon'
import PrintIcon from '../../assets/PrintIcon'
import { useCaptureScreenshot } from '../../common/hooks/useCaptureScreenshot'
import { useGeneratePDF } from '../../common/hooks/useGeneratePDF'
import PhoneIcon from './../../assets/PhoneIcon'
import currencyFormatter from './../../common/utils/currencyFormatterUtils'
import Config from '../../config/config'

function ViewExchangeReturn({ open, handleDrawerToggle, data, branchData, loginData }) {
  const navigate = useNavigate()
  const { ref, isPrinting, isDownloading, generatePDF } = useGeneratePDF()
  const { isCapturing, captureScreenshot } = useCaptureScreenshot(ref)

  const getDiscountedAmount = () => {
    return (
      (data.discount / 100) *
      (data.new_amount ? data.new_amount : data.old_amount)
    )
  }

  const getTradeDiscountedAmount = () => {
    return (
      (data.trade_discount / 100) *
      (data.new_amount ? data.new_amount : data.old_amount)
    )
  }

  const getVatAmount = () => {
    let discount = getDiscountedAmount() || 0
    let tradeDiscount = getTradeDiscountedAmount() || 0

    let remainingAmount =
      (data.new_amount ? data.new_amount : data.old_amount) -
      discount -
      tradeDiscount

    let vatPercentage = data.vat || 0

    return (vatPercentage / 100) * remainingAmount
  }

  // Determine if it's Exchange or Return flow
  const isExchangeFlow =
    data.reason_type === ADD_EXCHANGE_RETURN_FLOW.EXCHANGE_FLOW

  return (
    <SideDrawer
      handleDrawerToggle={handleDrawerToggle}
      open={open}
      drawerHeading={EXCHANGE_RETURN_UI_NAME[data.reason_type]}
    >
      <div className="viewProductWrap exchangeReturn">
        <div ref={ref} className="thermal-receipt-container">
          {/* Receipt Header */}
          <div className="receipt-header">
            <div className="receipt-title">
              {EXCHANGE_RETURN_UI_NAME[data.reason_type]}
            </div>

            {/* Branch Info */}
            <div className="receipt-branch-info">
              <div className="branch-info-row">
                {(branchData?.logo || data?.branch_details?.logo || data?.branch?.logo) && (
                  <img
                    src={branchData?.logo || Config.getFullImageUrl(data?.branch_details?.logo || data?.branch?.logo)}
                    alt="logo"
                    className="branch-logo"
                  />
                )}
                <div className="branch-details">
                  <div className="branch-name-phone">
                    <span className="branch-name">
                      {loginData?.pharmacy_name || branchData?.pharmacy_name || data?.branch_details?.pharmacy_name || ''}
                      {(branchData?.name || data?.branch_details?.name) ? ` (${branchData?.name || data?.branch_details?.name})` : ''}
                    </span>
                    <span className="branch-phone">📞 {branchData?.mobile_no || data?.branch_details?.mobile_no}</span>
                  </div>
                  <div className="branch-address">
                    📍 {branchData?.address || data?.branch_details?.address || branchData?.location}
                  </div>
                </div>
              </div>
            </div>

            <div className="receipt-separator">
              - - - - - - - - - - - - - - - - - - - -
            </div>
            <div className="receipt-invoice-no">
              Invoice: {data.invoice_number}
            </div>
            <div className="receipt-old-invoice">
              Old Invoice: {data.old_invoice_number}
            </div>
            <div className="receipt-datetime">
              {formatDate(data.created_at)} {formatTime(data.created_at)}
            </div>
            <div className="receipt-separator">
              - - - - - - - - - - - - - - - - - - - -
            </div>
          </div>

          {/* Customer Info */}
          <div className="receipt-customer">
            <div className="receipt-customer-name">
              Customer: {data?.customer?.name}
            </div>
            <div className="receipt-customer-phone">
              Phone: {data?.customer?.mobile_no}
            </div>
            <div className="receipt-customer-address">
              Address: {data?.customer?.shipping_address}
            </div>
          </div>

          {/* Products Section - RETURN/EXCHANGE Products */}
          <div className="receipt-products">
            <div className="receipt-section-title">
              {`${EXCHANGE_RETURN_UI_NAME[data.reason_type]} PRODUCTS`}
            </div>
            <div className="dotter">
              - - - - - - - - - - - - - - - - - - - - - - - - -
            </div>

            <table className="receipt-product-table">
              <thead>
                <tr>
                  <th className="col-item-header">ITEM NAME</th>
                  <th className="col-qty-header">QTY</th>
                  <th className="col-qty-header">
                    {isExchangeFlow ? '' : 'RETURN QTY'}
                  </th>
                  <th className="col-amount-header">
                    {' '}
                    {isExchangeFlow ? 'AMOUNT' : 'OLD AMOUNT'}{' '}
                  </th>
                  <th className="col-amount-header">
                    {isExchangeFlow ? '' : 'RETURN AMOUNT'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.product_details?.map((product, index) => {
                  if (product.type === 'NEW') return null

                  return (
                    <tr key={index}>
                      <td className="col-item-data">
                        {product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME]}{' '}
                        {product?.[PRODUCT_DETAIL_FIELDS.DOSE]}
                      </td>
                      <td className="col-qty-data">{product.old_quantity}</td>
                      <td className="col-qty-data">
                        {product.new_quantity &&
                          product.new_quantity !== product.old_quantity && (
                            <> {product.new_quantity}</>
                          )}
                      </td>
                      <td className="col-amount-data">
                        {product.rate_per_unit * product.old_quantity}
                      </td>
                      <td className="col-amount-data">
                        {product.new_quantity &&
                          product.new_quantity !== product.old_quantity && (
                            <> {product.rate_per_unit * product.new_quantity}</>
                          )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div className="dotter">
              - - - - - - - - - - - - - - - - - - - - - - - - -
            </div>
            <div className="receipt-subtotal">
              <span className="subtotal-label">
                {EXCHANGE_RETURN_UI_NAME[data.reason_type]} PRODUCT VALUE:
              </span>
              <span className="subtotal-value">
                BDT {currencyFormatter(data.old_amount)}
              </span>
            </div>
          </div>

          {/* New Products for Exchange */}
          {data.reason_type === ADD_EXCHANGE_RETURN_FLOW.EXCHANGE_FLOW && (
            <div className="receipt-products">
              <div className="dotter">
                - - - - - - - - - - - - - - - - - - - - - - - - -
              </div>
              <div className="receipt-section-title">
                NEW PRODUCTS (EXCHANGE)
              </div>
              <div className="dotter">
                - - - - - - - - - - - - - - - - - - - - - - - - -
              </div>

              <table className="receipt-product-table">
                <thead>
                  <tr>
                    <th className="col-item-header">ITEM NAME</th>
                    <th className="col-qty-header">QTY</th>
                    <th className="col-amount-header">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.product_details?.map((product, index) => {
                    if (product.type !== 'NEW') return null

                    return (
                      <tr key={index}>
                        <td className="col-item-data">
                          {product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME]}{' '}
                          {product?.[PRODUCT_DETAIL_FIELDS.DOSE]}
                        </td>
                        <td className="col-qty-data">{product.new_quantity}</td>
                        <td className="col-amount-data">
                          BDT {product.rate_per_unit * product.new_quantity}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              <div className="dotter">
                - - - - - - - - - - - - - - - - - - - - - - - - -
              </div>
              <div className="receipt-subtotal">
                <span className="subtotal-label">NEW PRODUCT VALUE:</span>
                <span className="subtotal-value">
                  BDT {currencyFormatter(data.new_amount_total)}
                </span>
              </div>
            </div>
          )}

          {/* Totals Section */}
          <div className="receipt-totals">
            <div className="dotter">
              - - - - - - - - - - - - - - - - - - - - - - - - -
            </div>
            <div className="receipt-calculation-label">SUMMARY</div>

            <div className="receipt-total-row">
              <span className="total-label">Subtotal Amount:</span>
              <span className="total-value">
                {isExchangeFlow ? (
                  <>
                    {' '}
                    BDT{' '}
                    {currencyFormatter(
                      data.new_amount ? data.new_amount : data.old_amount
                    )}{' '}
                    - {currencyFormatter(data.old_amount)}
                  </>
                ) : (
                  <>
                    BDT{' '}
                    {data?.product_details
                      ?.filter((product) => product.type !== 'NEW')
                      .reduce((total, product) => {
                        const amount =
                          product.new_quantity &&
                          product.new_quantity !== product.old_quantity
                            ? product.rate_per_unit * product.new_quantity
                            : product.rate_per_unit * product.old_quantity
                        return total + amount
                      }, 0)}
                  </>
                )}
              </span>
            </div>

            <div className="receipt-total-row">
              <span className="total-label">
                Discount ({data.discount || 0}%):
              </span>
              <span className="total-value discount">
                - BDT {currencyFormatter(getDiscountedAmount())}
              </span>
            </div>

            <div className="receipt-total-row">
              <span className="total-label">VAT ({data.vat || 0}%):</span>
              <span className="total-value">+ BDT {getVatAmount()}</span>
            </div>

            <div className="dotter">
              - - - - - - - - - - - - - - - - - - - - - - - - -
            </div>

            <div className="receipt-grand-total">
              <span className="grand-total-label">TOTAL PAYABLE:</span>
              <span
                className={`grand-total-value ${data.total_amount > 0 ? 'positive-amount' : data.total_amount < 0 ? 'negative-amount' : ''}`}
              >
                BDT {currencyFormatter(Math.abs(data.total_amount))}
                {data.total_amount < 0 && (
                  <span className="refund-tag"> (REFUND)</span>
                )}
              </span>
            </div>
          </div>
          <div className="receipt-footer">
            <div className="receipt-separator">
              - - - - - - - - - - - - - - - - - - - - - - - - --
            </div>
          </div>

          {/* Payment Mode */}
          {data.payment_mode && (
            <div className="receipt-payment">
              <div className="receipt-payment-info">
                <span className="payment-label">PAYMENT MODE:</span>
                <span className="payment-value">
                  {PAYMENT_MODE_OPTIONS.find(
                    (item) => item.name === data.payment_mode
                  )?.label || data?.payment_mode}
                </span>
              </div>
              <div className="dotter">
                - - - - - - - - - - - - - - - - - - - - - - - - - -
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="receipt-footer">
            <div className="notes-heading">Important Notice:</div>
            <div className="notes-content">
              বিঃদ্রঃ সেলস রিসিট ব্যতীত বিক্রিত পণ্য ফেরত বা পরিবর্তন করা হয়
              না। ক্রয়কৃত পণ্য ৭ (সাত) দিনের মধ্যে পরিবর্তনযোগ্য। তবে পাতা কাটা
              ওষুধ, ইন্সুলিন, ভ্যাক্সিন এবং সুগার টেস্ট স্ট্রিপ ফেরত বা
              পরিবর্তনযোগ্য নয়।
            </div>
            <div className="receipt-separator">
              - - - - - - - - - - - - - - - - - - - -
            </div>
            <div className="receipt-footer-info">
              <div className="footer-software">
                <span className="software-label">Software By : DG ePharma</span>

                <PhoneIcon className="phone-icon w-2" />
                <span className="contact-number">09606501155</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="actionButton mt-5 dFlex fdColumn ai-flex-end">
          <div className="download-print-button-group">
            <button
              className="download-print-button download"
              style={{
                cursor: isDownloading || isPrinting ? 'not-allowed' : 'pointer',
                opacity: isDownloading || isPrinting ? 0.6 : 1,
              }}
              onClick={() => captureScreenshot('exchange-receipt.pdf')}
              disabled={isDownloading || isPrinting}
            >
              <DownloadIcon />
              <span className="downloadButtonMargin">Download</span>
            </button>

            <button
              className="download-print-button print"
              style={{
                cursor: isDownloading || isPrinting ? 'not-allowed' : 'pointer',
                opacity: isDownloading || isPrinting ? 0.6 : 1,
              }}
              onClick={() => generatePDF(true)}
              disabled={isDownloading || isPrinting}
            >
              <PrintIcon />
              <span className="printButtonMargin">Print</span>
            </button>
          </div>
        </div>
      </div>
    </SideDrawer>
  )
}

const mapStateToProps = (state) => {
  return {}
}
const mapDispatchToProps = (dispatch) => {
  return {}
}

ViewExchangeReturn.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewExchangeReturn)
